import React, { useState, useEffect, useContext } from 'react'
import Layout from 'components/layout'
import SEO from 'components/seo'
import AnimatedHeroText from 'components/animatedHeroText'
import { graphql } from 'gatsby'
import { injectIntl, Link } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import { colors, breakpoints, richTextOptions as options, transitions } from 'utils/utils'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import 'animate.css/animate.min.css'
import ScrollAnimation from 'react-animate-on-scroll'
import HeroContainer from 'components/heroContainer'
import { IoIosArrowRoundForward, IoIosSync } from 'react-icons/io'
import { GlobalStateContext } from '../context/globalContext'
import { GlobalDispatchContext } from '../context/globalContext'

export default injectIntl(props => {

    const locale = props.intl.locale

    const state = useContext(GlobalStateContext)
    const dispatch = useContext(GlobalDispatchContext)  
     
    useEffect(() => {
        if(state.otherLanguageSlug !== props.pageContext.otherLanguageSlug) {
            dispatch(state.changeOtherLanguageSlug(props.pageContext.otherLanguageSlug))
        }
    }, [state, dispatch, props.pageContext.otherLanguageSlug])

    const [ isOptionSelected, setIsOptionSelected ] = useState(false)
    
    const { media, data, seo, title, cta, animatedHeroText, listBlockSection } = props.data.contentfulSection
    
    const blogEntries = props.data.blogEntries

    const handleTitleClick = (e) => {
        setIsOptionSelected(true)
        document.querySelector(':focus').blur()
    }

    useEffect(() =>{
        if(typeof props.location.state !== 'undefined'){
            if(props.location.state && typeof props.location.state.isOptionSelected !== 'undefined') {
                setIsOptionSelected(props.location.state.isOptionSelected)
            }
        }
    },[props.location.state])

    return (
        < >
            <Layout>
                <SEO 
                    title={seo.title}
                    keywords={seo.attributes[0].keywords}
                    slug={seo.slug}
                    description={seo.attributes[0].description}
                    lang={props.pageContext.language}
                />            
                <Container>
                    <HeroContainer arrowHide={isOptionSelected ? '' : 'hide'}>
                        { isOptionSelected ? 
                            <AnimatedHeroText data={animatedHeroText} image={media[0].fluid} /> 
                        : 
                            <Title className="heroOption">
                                <Link to={`/${locale}/${cta[0].href}`} key={cta[0].id}>
                                    <h1>
                                        <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                            {cta[0].value}
                                        </ScrollAnimation>
                                    </h1>
                                </Link>
                            </Title>
                        }
                        {!isOptionSelected &&
                            <ArrowSeparator>
                                <IoIosSync />
                            </ArrowSeparator>
                        }
                        <Title className={isOptionSelected ? '' : 'heroOption'}>
                            { isOptionSelected ? 
                                <>
                                    <Section animateOnce={true} offset={0} animateIn="fadeInRightBig">
                                        <IoIosArrowRoundForward />
                                        <Link to={`/${locale}/${cta[0].href}`} key={cta[0].id}>
                                            {cta[0].value}
                                        </Link>
                                    </Section>
                                    <h1>
                                        {title}
                                    </h1>
                                </>
                            : 
                                <button aria-label={title} onClick={handleTitleClick}>
                                    <h1>
                                        <ScrollAnimation animateOnce={true} offset={0} animateIn="fadeInUp">
                                            {title}
                                        </ScrollAnimation>
                                    </h1>
                                </button>
                            }
                        </Title>
                    </HeroContainer>
                    { isOptionSelected ? 
                    <>
                        <ListBlockSection>
                            {
                                listBlockSection.map((listBlock, index) => {
                                    return (
                                        <ListBlock key={index} animateOnce={true} animateIn='fadeInUp' delay={200}>
                                            <h2>{listBlock.title}</h2>
                                            <div>
                                                { documentToReactComponents(listBlock.text && listBlock.text.json, options) }
                                            </div>
                                        </ListBlock>
                                    )
                                })
                            }
                        </ListBlockSection>
                        <BlogBlock>
                            <BlogTitle>
                                <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                    {data.blogSectionTitle}
                                </ScrollAnimation>
                            </BlogTitle>
                            <BlogEntries>
                                {
                                    blogEntries.edges.map(({node: entry},index) => {
                                        return(
                                            <BlogEntry key={index} to={(`/${locale}/${entry.slug}`).toLowerCase().trim()}>
                                                <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                                    <h2>{entry.title}</h2>
                                                    {entry.summary ? <p>{entry.summary.summary}</p> : ''}
                                                </ScrollAnimation>
                                            </BlogEntry>
                                        )
                                    })
                                }
                            </BlogEntries>
                        </BlogBlock>
                    </>
                    : ''}
                </Container>
            </Layout>
        </>
    )
})

const Section = styled(ScrollAnimation)`
    position: absolute;
    top: 15%;
    right: 10%; 
    font-size: 1rem;
    text-align: center;
    min-width: max-content;
    color: ${colors.primary};
    cursor: pointer;
    margin: 2rem 2rem;
    text-transform: uppercase;
    letter-spacing: .1rem;
    font-weight: 700;
    transition: all 0.3s;
    ${breakpoints.md} {
        top: unset;
        right: unset;
        left: 10%;
        bottom: 10%;
    }
    ${breakpoints.sm} {
        margin-left: 0;
        left: .5rem;
        bottom: 10%;
        font-size: .9rem;
    }
    
    a:after {
        content: '';
        position: absolute;
        width: 1.3rem;
        height: 1.3rem;
        background-color: ${colors.secondary};
        z-index: 1;
        right: 4rem;
        top: 30%;
        margin: 0 -.8rem -.8rem 0;
        transform: rotate(45deg);
    }

    a:before, svg {
        content: '';
        position: absolute;
        height: 100%;
        right: 0;
        top: 0;
        line-height: 3;
        width: 4rem;
        z-index: 2;
        border-radius: 0 .8rem .8rem 0;
    }
    svg {
        width: 4rem;
        right: 0;
        margin: auto;
        color: ${colors.quinary}CC;
        background-color: ${colors.secondary};
    }
    a {
        display: block;
        padding: 1rem 6rem 1rem 1.5rem;
        background-color: ${colors.quinary}CC;
        border-radius: .8rem;
        font-weight: 500;
    }
    &:hover {
        svg {
            color: ${colors.quinary};
            transition: color .5s;
        }
        a {
            background-color: ${colors.quinary};
            transition: background-color .5s;
            &:active, &:visited {
                color: ${colors.primary};
            }
            font-weight: 900;
        }
    }
`
const ArrowSeparator = styled.button`
    content: '';
    position: absolute;
    height: 3rem;
    width: 3rem;
    bottom: calc(50% - 1.5rem);
    left: calc(50% - 1.5rem);
    z-index: 1;
    padding: 0!important;
    border: 0;
    background-color: transparent;
    cursor: pointer;
    display: none;
    ${breakpoints.md} {
        display: initial;
    }

    @keyframes MoveUpDown {
        0%, 50%, 100% {
            top: 0;
        }
        25% {
            top: 20%;
        }
        75% {
            top: -20%;
        }
    }

    svg {
        background-color: ${colors.secondary}; 
        border: solid .1rem ${colors.senary};
        border-radius: 50%;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        right: 0;
        animation: MoveUpDown 2s linear infinite;
    }
`

const BlogEntry = styled(Link)`
    width: 33.3333%;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    padding: 1.5rem 3rem;
    background-color: ${colors.tertiary}; 
    color: ${colors.quaternary};
    text-decoration: none;
    ${breakpoints.md} {
        width: 100%;
    }
    &:nth-child(1) {
        background-color: ${colors.primary}; 
        color: ${colors.tertiary};
    }
    &:nth-child(3) {
        background-color: ${colors.secondary};
        color: ${colors.tertiary};
    }
    h2 {
        letter-spacing: .2rem;
        font-size: 1.4rem;
    }
    p {
        font-size: 1rem;
    }
    h2, p {
        transition: ${transitions.button};
    }
    &:hover {
        h2, p {
            transform: scale(1.1);
        }
    }
`

const BlogEntries = styled.div`
    display: flex;
    flex-direction: row;
    min-height: 10rem;
    ${breakpoints.md} {
        flex-wrap: wrap;
    }
`

const BlogBlock = styled.div`
    width: 100%;
    background-color: ${colors.quaternary}; 
    color: ${colors.tertiary}; 
`
const BlogTitle = styled.div`
    padding: 1rem 0;
    margin: auto;
    width: fit-content;
    font-size: 4rem;
    font-weight: bold;
    text-align: center;
    ${breakpoints.md} {
        font-size: 2.5rem;
    }
`
const ListBlockSection = styled.div`
    width: 60%;
    margin: auto;
    ${breakpoints.md} {
        width: 100%;
    }
    ul {
        list-style-type: disc;
    }
`
const ListBlock = styled(ScrollAnimation)`
    padding: 2rem 2rem 0;
    margin: auto;
    white-space: pre-wrap;
    h2 {
        font-size: 2.1rem;
    }
    &:last-of-type {
        padding-bottom: 2rem;
    }
`

const Container = styled.div`
    background-color: ${colors.quinary};
`

const Title = styled.div`
    position: relative; 
    width: 50%;
    background-color: ${colors.primary};    
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    transition: all .5s ease-in-out;

    ${breakpoints.md} {
        width: 100%;
        // min-height: 30rem;
        height: 50vh;
        order: 1;
        .show {
            max-width: 15rem;
        }
        border-bottom: 2px solid ${colors.secondary};
    }
    &.heroOption:hover {
        background-color: ${colors.secondary}F3;
        h1 {
            transform: scale(1.2);
            
            div {
                animation: none;
            }
        }
    }
    h1 {
        font-size: 4rem;
        color: ${colors.tertiary};
        max-width: 70%;
        margin: auto;
        font-size 3.5rem;
        transition: all .5s ease-in-out;
        text-align: center;
        ${breakpoints.md} {
            margin: auto;
            max-width: 90%;
            text-align: center;
            font-size 2.5rem;
        }

        @keyframes Vibrate {
            0%, 100% {
                opacity: 1;
                transform: scale(1);
            }
            50% {
                opacity: .9;
                transform: scale(1.01);
            }
        }

        div {
            animation: Vibrate 200s linear infinite;
        }
    }
    button {
        background: none;
        border: 0;
        text-align: left;
        cursor: pointer;
        padding: 0;
        width: 100%;
        height: 100%;
    }
    a {
        text-decoration: none;
        width: 100%;
        height: 100%;
        display: flex;
    }
`

export const query = graphql`
    query($language: String) {
        contentfulSection(name: {eq: "B2C"}, node_locale: {eq: $language}) {
            id
            title
            seo {
                title
                slug
                attributes {
                    description
                    keywords
                }
            }
            data {
                blogSectionTitle
            }
            media {
                id
                title
                fluid(quality: 90, maxWidth: 1920) {
                    ...GatsbyContentfulFluid_withWebp_noBase64            
                }
            }
            cta {
                id
                href
                value
            }
            animatedHeroText {
                text1
                text2
                text3
                text4
                text5
                text6
            }
            listBlockSection {
                title
                text {
                    json
                }
            }
        }
        blogEntries: allContentfulBlogEntry(sort: {fields: createdAt, order: DESC}, limit: 3, filter: {tags: {eq: "b2c"}, node_locale: {eq: $language}}) {
            edges {
                node {
                    id
                    title
                    slug
                    summary {
                        summary
                    }
                }
            }
        }
    }

`
